import React from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Collapse,
  Icon,
  useDisclosure
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { motion } from 'framer-motion';

const MotionBox = motion(Box);
const MotionVStack = motion(VStack);

/**
 * Curriculum Timeline Component
 * Renders vertical timeline with expandable subjects and lessons
 */
const CurriculumTimeline = ({ timeline }) => {
  if (!timeline || timeline.length === 0) return null;

  return (
    <VStack spacing={0} align="stretch" w="full" position="relative">
      {/* Timeline line - appears behind subjects */}
      <Box
        position="absolute"
        left={{ base: '15px', md: '23px' }}
        top="40px"
        bottom="0"
        w="2px"
        bg="linear-gradient(180deg, #FF6B35, #004E89)"
        opacity="0.3"
        zIndex={0}
      />

      {/* Timeline items */}
      {timeline.map((subject, subjectIndex) => (
        <TimelineSubject
          key={subject.subjectId}
          subject={subject}
          index={subjectIndex}
          isLast={subjectIndex === timeline.length - 1}
        />
      ))}
    </VStack>
  );
};

/**
 * Individual Subject in Timeline
 */
const TimelineSubject = ({ subject, index, isLast }) => {
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: index === 0 });

  const { sequenceNumber, subjectTitle, lessons } = subject;

  return (
    <MotionBox
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration: 0.4, delay: index * 0.1 }}
      position="relative"
      zIndex={1}
      mb={6}
    >
      {/* Timeline dot and content */}
      <HStack align="flex-start" spacing={4}>
        {/* Timeline dot */}
        <Box
          flexShrink={0}
          w={12}
          h={12}
          borderRadius="full"
          bg="white"
          border="3px solid"
          borderColor="brand.600"
          display="flex"
          alignItems="center"
          justifyContent="center"
          fontWeight="900"
          fontSize="lg"
          color="brand.600"
          boxShadow="0 2px 8px rgba(0,0,0,0.1)"
          mt={2}
        >
          {sequenceNumber}
        </Box>

        {/* Subject Card */}
        <Box
          flex={1}
          bg="white"
          borderRadius="lg"
          border="1px solid"
          borderColor="gray.200"
          overflow="hidden"
          transition="all 0.2s"
          _hover={{
            borderColor: 'brand.600',
            boxShadow: '0 4px 12px rgba(255, 107, 53, 0.15)',
            transform: 'translateY(-2px)'
          }}
          cursor="pointer"
          onClick={onToggle}
        >
          {/* Subject Header */}
          <Box
            p={4}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            bg={isOpen ? 'brand.50' : 'white'}
            transition="background-color 0.2s"
          >
            <VStack align="start" spacing={1}>
              <Text
                fontSize="lg"
                fontWeight="700"
                color="gray.900"
              >
                {subjectTitle}
              </Text>
              <Text fontSize="sm" color="gray.600">
                {lessons.length} {lessons.length === 1 ? 'lesson' : 'lessons'}
              </Text>
            </VStack>

            {/* Expand Icon */}
            <Icon
              as={ChevronDownIcon}
              w={6}
              h={6}
              color="brand.600"
              transform={isOpen ? 'rotate(180deg)' : 'rotate(0deg)'}
              transition="transform 0.2s"
              flexShrink={0}
            />
          </Box>

          {/* Lessons List - Collapsible */}
          <Collapse in={isOpen} animateOpacity>
            <VStack
              align="stretch"
              spacing={0}
              borderTop="1px solid"
              borderColor="gray.100"
              bg="white"
            >
              {lessons.map((lesson, lessonIndex) => (
                <LessonItem
                  key={lesson.lessonId}
                  lesson={lesson}
                  index={lessonIndex}
                  isLast={lessonIndex === lessons.length - 1}
                />
              ))}
            </VStack>
          </Collapse>
        </Box>
      </HStack>
    </MotionBox>
  );
};

/**
 * Individual Lesson Item
 */
const LessonItem = ({ lesson, index, isLast }) => {
  const { sequenceNumber, title } = lesson;

  return (
    <MotionBox
      initial={{ opacity: 0, x: -10 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      p={4}
      display="flex"
      alignItems="center"
      gap={3}
      borderBottom={!isLast ? '1px solid' : 'none'}
      borderColor="gray.100"
      _hover={{ bg: 'brand.50' }}
      transition="background-color 0.2s"
    >
      {/* Lesson dot */}
      <Box
        flexShrink={0}
        w={8}
        h={8}
        borderRadius="full"
        bg="brand.100"
        display="flex"
        alignItems="center"
        justifyContent="center"
        fontSize="xs"
        fontWeight="700"
        color="brand.600"
      >
        {sequenceNumber}
      </Box>

      {/* Lesson title */}
      <Text
        fontSize="sm"
        color="gray.700"
        flex={1}
        lineHeight="1.4"
      >
        {title}
      </Text>
    </MotionBox>
  );
};

export default CurriculumTimeline;
